import { toast } from 'sonner'
import type { ApiEnvelope } from '../types'
import { AuthService } from './authService'

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  'https://britfiler-backend-w5rjg.ondigitalocean.app/api'
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || '10000')

// Flag to prevent multiple logout calls
let isLoggingOut = false

// Import AuthService for token management
const getAccessToken = (): string | null => {
  return localStorage.getItem('accessToken')
}

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: any
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

interface RequestOptions extends RequestInit {
  timeout?: number
  /**
   * When true, skip automatic logout/refresh on 401 for this request.
   * Useful for non-critical background checks (e.g., payment success page).
   */
  skipAuthLogout?: boolean
}

class ApiClient {
  private baseURL: string
  private defaultTimeout: number

  constructor(baseURL: string = API_BASE_URL, timeout: number = API_TIMEOUT) {
    this.baseURL = baseURL
    this.defaultTimeout = timeout
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<ApiEnvelope<T>> {
    // Don't make requests if we're already logging out
    if (isLoggingOut) {
      throw new ApiError('Logout in progress', 401)
    }

    const { timeout = this.defaultTimeout, skipAuthLogout = false, ...fetchOptions } = options

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    // Prepare headers with authentication
    const baseHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
    }

    // Add Bearer token if available
    const accessToken = getAccessToken()
    if (accessToken) {
      baseHeaders.Authorization = `Bearer ${accessToken}`
    }

    const headers = {
      ...baseHeaders,
      ...(fetchOptions.headers as Record<string, string>),
    }

    try {
      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...fetchOptions,
        signal: controller.signal,
        headers,
      })

      clearTimeout(timeoutId)

      const contentType = response.headers.get('Content-Type') || ''

      if (!response.ok) {
        const errorData = await (contentType.includes('application/json')
          ? response.json().catch(() => ({}))
          : Promise.resolve({}))
        const errorMessage =
          errorData?.response?.message ||
          errorData?.message ||
          errorData?.error ||
          `HTTP ${response.status}`
        
        // Handle token expiration (401 Unauthorized)
        // Skip handling for auth endpoints (login, signup, etc.) as 401 is expected there
        const isAuthEndpoint = endpoint.startsWith('/auth/')
        const isRefreshEndpoint = endpoint === '/auth/refresh-token'
        const isHmrcAuthEndpoint = endpoint.startsWith('/filling/auth/')
        const isHmrcAuthError =
          typeof errorMessage === 'string' && /hmrc/i.test(errorMessage)
        
        if (response.status === 401 && !isAuthEndpoint && !isLoggingOut) {
          // If caller explicitly opts out, do not refresh/logout - check this FIRST
          // This prevents HMRC redirects and token refresh attempts
          if (skipAuthLogout) {
            throw new ApiError(
              typeof errorMessage === 'string' ? errorMessage : 'Unauthorized',
              response.status,
              errorData
            )
          }
          
          // Only check HMRC errors if skipAuthLogout is false
          if (isHmrcAuthEndpoint || isHmrcAuthError) {
            toast.error('HMRC authorization expired. Please re-authorize to continue.', {
              duration: 5000,
            })
            window.location.replace('/dashboard')
            throw new ApiError(
              typeof errorMessage === 'string' ? errorMessage : 'HMRC authorization required',
              response.status,
              errorData
            )
          }
          // Try to refresh token before logging out
          const refreshToken = localStorage.getItem('refreshToken')
          
          if (refreshToken && !isRefreshEndpoint) {
            try {
              // Attempt to refresh the token
              const refreshResponse = await fetch(`${this.baseURL}/auth/refresh-token`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refreshToken }),
              })
              
              if (refreshResponse.ok) {
                const refreshData = await refreshResponse.json()
                const tokens = refreshData?.response || refreshData
                // Backend returns: { accessToken, refreshToken, user } (possibly wrapped in response)
                const { accessToken: newAccessToken, refreshToken: newRefreshToken } = tokens
                
                if (!newAccessToken || !newRefreshToken) {
                  throw new Error('Invalid refresh response')
                }
                
                // Update tokens in storage (both are rotated)
                localStorage.setItem('accessToken', newAccessToken)
                localStorage.setItem('refreshToken', newRefreshToken)
                
                // Retry the original request with new token
                const retryHeaders = {
                  ...headers,
                  Authorization: `Bearer ${newAccessToken}`,
                }
                
                const retryResponse = await fetch(`${this.baseURL}${endpoint}`, {
                  ...fetchOptions,
                  headers: retryHeaders,
                })
                
                clearTimeout(timeoutId)
                
                if (retryResponse.ok) {
                  const retryContentType = retryResponse.headers.get('Content-Type') || ''
                  
                  if (retryContentType.includes('application/json')) {
                    const json = (await retryResponse.json()) as Record<string, any>
                    return {
                      statusCode: (json.statusCode as number | undefined) ?? retryResponse.status,
                      response: (json.response ?? json) as T,
                      message: json.message,
                      error: json.error,
                    }
                  }
                  
                  return {
                    statusCode: retryResponse.status,
                    response: (await retryResponse.blob()) as unknown as T,
                  }
                }
              }
            } catch (refreshError) {
              // Refresh failed, proceed with logout
              console.error('Token refresh failed:', refreshError)
            }
          }
          
          // If refresh failed or no refresh token, logout
          isLoggingOut = true
          
          // Show toast notification only once
          toast.error('Your session has expired. Please log in again.', {
            duration: 5000,
          })
          
          // Clear tokens and redirect to login after a short delay to allow toast to be visible
          AuthService.logout(1000)
          
          throw new ApiError(
            'Token expired. Please log in again.',
            response.status,
            errorData
          )
        }
        
        throw new ApiError(
          typeof errorMessage === 'string'
            ? errorMessage
            : 'Request failed',
          response.status,
          errorData
        )
      }

      if (contentType.includes('application/json')) {
        const json = (await response.json()) as Record<string, any>
        return {
          statusCode:
            (json.statusCode as number | undefined) ?? response.status,
          response: (json.response ?? json) as T,
          message: json.message,
          error: json.error,
        }
      }

      // For non-JSON responses (e.g., iXBRL blobs), return raw data
      return {
        statusCode: response.status,
        response: (await response.blob()) as unknown as T,
      }
    } catch (error) {
      clearTimeout(timeoutId)

      if (error instanceof ApiError) {
        throw error
      }

      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError('Request timeout', 408)
      }

      throw new ApiError('Network error', 0, error)
    }
  }

  async get<T>(
    endpoint: string,
    options?: RequestOptions
  ): Promise<ApiEnvelope<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' })
  }

  async post<T>(
    endpoint: string,
    data?: any,
    options?: RequestOptions
  ): Promise<ApiEnvelope<T>> {
    const requestOptions: RequestOptions = {
      ...options,
      method: 'POST',
    }
    if (data) {
      requestOptions.body = JSON.stringify(data)
    }
    return this.request<T>(endpoint, requestOptions)
  }

  async put<T>(
    endpoint: string,
    data?: any,
    options?: RequestOptions
  ): Promise<ApiEnvelope<T>> {
    const requestOptions: RequestOptions = {
      ...options,
      method: 'PUT',
    }
    if (data) {
      requestOptions.body = JSON.stringify(data)
    }
    return this.request<T>(endpoint, requestOptions)
  }

  async delete<T>(
    endpoint: string,
    options?: RequestOptions
  ): Promise<ApiEnvelope<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' })
  }

  async patch<T>(
    endpoint: string,
    data?: any,
    options?: RequestOptions
  ): Promise<ApiEnvelope<T>> {
    const requestOptions: RequestOptions = {
      ...options,
      method: 'PATCH',
    }
    if (data) {
      requestOptions.body = JSON.stringify(data)
    }
    return this.request<T>(endpoint, requestOptions)
  }
}

// Export a default instance
export const api = new ApiClient()

// Export function to reset logout flag (used when user successfully logs in)
export const resetLogoutFlag = () => {
  isLoggingOut = false
}

export { ApiClient, ApiError }
